import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef } from 'react';
import PT from 'prop-types';
import { F as FeedbackFormState } from './FeedbackFormState-Bn3lk58_.js';
import { F as FeedbackFormLayout } from './FeedbackFormLayout-DoG7xZaY.js';
import { F as FeedbackFormThemeProvider } from './FeedbackFormThemeProvider-BtQQn72x.js';
import { l as locales } from './locales-DbFLN74b.js';
import { F as FeedbackFormPlaceholder } from './FeedbackFormPlaceholder-CT8njHa2.js';
import { I as IntlProvider } from './i18n/i18n-Z3LCXHeB.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

const NodeSafeHTMLElement = typeof HTMLElement !== 'undefined' ? HTMLElement : Object;
const FeedbackForm = /*#__PURE__*/forwardRef(({
  children,
  defaultValues,
  id,
  label,
  locale,
  onSubmit,
  onValidate,
  theme,
  isMobile,
  onLoadFailed,
  translationMap,
  ...feedbackFormLayoutProps
}, ref) => /*#__PURE__*/React__default.createElement(FeedbackFormState, {
  id: id,
  label: label,
  onSubmit: onSubmit,
  onValidate: onValidate,
  defaultValues: defaultValues
}, /*#__PURE__*/React__default.createElement(FeedbackFormThemeProvider, {
  theme: theme,
  isMobile: isMobile
}, /*#__PURE__*/React__default.createElement(IntlProvider, {
  translationMap: translationMap,
  onLoadFailed: onLoadFailed,
  locale: locale,
  fallback: /*#__PURE__*/React__default.createElement(FeedbackFormPlaceholder, _extends({
    theme: theme,
    isMobile: isMobile
  }, feedbackFormLayoutProps))
}, /*#__PURE__*/React__default.createElement(FeedbackFormLayout, _extends({}, feedbackFormLayoutProps, {
  ref: ref
}), children)))));
FeedbackForm.propTypes = {
  /**
   * Pass a ref to an element here to use as the anchor element. Focus will move back to this
   * element when the form closes.
   */
  anchorElement: PT.shape({
    current: PT.instanceOf(NodeSafeHTMLElement)
  }).isRequired,
  /**
   * Label for the cancel button.
   */
  cancelButtonLabel: PT.string,
  /**
   * The React nodes that are rendered by the FeedbackForm.
   * The nodes need to be wrapped by FeedbackFormField.
   * It can also be a render prop function with parameter formContext (FieldChildrenProps)
   */
  children: PT.oneOfType([PT.node, PT.func]).isRequired,
  /**
   * Screen reader-only label for the close button (Desktop mode).
   */
  closeButtonLabel: PT.string,
  /**
   * Messsage showed in the submit button tooltip if there is no user input yet,
   * or if users didn't complete the mandatory field
   */
  defaultValidationErrorMessage: PT.string,
  /**
   * Used as the initial value for inputs when a component is first rendered,
   * before a user interacts with it.
   * format: { input_name: value }
   */
  defaultValues: PT.objectOf(PT.oneOfType([PT.bool, PT.number, PT.string])),
  /**
   * The unique id attribute that is supplied to the form element.
   */
  id: PT.string.isRequired,
  /**
   * Whether the FeedbackForm Intro is skipped (Mobile mode).
   */
  isIntroSkipped: PT.bool,
  /**
   * Whether the FeedbackForm is rendered with mobile theme.
   */
  isMobile: PT.bool,
  /**
   * Whether the FeedbackForm is rendered as a modal or a popper.
   */
  type: PT.oneOf(['inline', 'modal', 'popper']),
  /**
   * Whether the FeedbackForm is visible.
   */
  isOpen: PT.bool.isRequired,
  /**
   * Screen reader-only label for the form.
   */
  label: PT.string.isRequired,
  /**
   * The current language for localization.
   * Supported options: https://w.amazon.com/bin/view/INTech/Panther/I18nFamily#Hadvertising
   */
  locale: PT.oneOf(locales),
  /**
   * Label for the close button (Mobile mode).
   */
  mobileCloseButtonLabel: PT.string,
  /**
   * Label for the share button (Mobile mode).
   */
  mobileShareButtonLabel: PT.string,
  /**
   * Function to perform if resubmit button is clicked. Never invoked unless resubmitEnabled is true.
   */
  onClickResubmit: PT.func,
  /**
   * Function to perform on form close.
   */
  onClose: PT.func,
  /**
   * Function to perform on translations load failed.
   */
  onLoadFailed: PT.func,
  /**
   * Function to perform on translations load succeeded.
   */
  onLoadSucceeded: PT.func,
  /**
   * Function to perform on form submit.
   * It returns a promise. If the promise resolves to null, then the form was submitted successful.
   * Throwing an error results in the user being shown a message that feedback can not be submitted.
   */
  onSubmit: PT.func.isRequired,
  /**
   * Validation Function to perform on inputs change.
   * - Make sure you are returning an object that has both a values and an errors property.
   *   Their default values should be an empty object. For example: {}.
   * - The keys of the error object should match the name values of your fields.
   */
  onValidate: PT.func.isRequired,
  /**
   *  the offsets needed to position the popper element near the reference element.
   *  https://popper.js.org/docs/v2/modifiers/offset/
   */
  popperOffset: PT.oneOfType([PT.func, PT.arrayOf(PT.number)]),
  /**
   *  the preferred placement of the popper.
   *  https://popper.js.org/docs/v2/constructors/#placement
   */
  popperPlacement: PT.string,
  /**
   *  the strategy used to position the popper.
   *  https://popper.js.org/docs/v2/constructors/#strategy
   */
  popperStrategy: PT.string,
  /**
   * The disclaimer text that is rendered by the FeedbackForm.
   */
  renderDisclaimer: PT.oneOfType([PT.string, PT.func]),
  /**
   * The heading text that is rendered by the FeedbackForm.
   */
  renderHeader: PT.oneOfType([PT.string, PT.func]),
  /**
   * The intro text that is rendered by the FeedbackForm (Mobile mode).
   */
  renderIntro: PT.oneOfType([PT.string, PT.func]),
  /**
   * The prompt text that is rendered by the FeedbackForm.
   */
  renderPrompt: PT.oneOfType([PT.string, PT.func]),
  /**
   * The message that is rendered by the FeedbackForm if submission success.
   */
  renderSuccess: PT.oneOfType([PT.string, PT.func]),
  /**
   * The message that is rendered by the FeedbackForm if submission failed.
   */
  renderError: PT.oneOfType([PT.string, PT.func]),
  /**
   * Whether to reset the form state on form close.
   */
  resetOnClose: PT.bool,
  /**
   * Label for the resubmit button.
   */
  resubmitButtonLabel: PT.string,
  /**
   * Whether resubmit is enabled.
   */
  resubmitEnabled: PT.bool,
  /**
   * Label for the submit button.
   */
  submitButtonLabel: PT.string,
  /**
   * Label for the submit button (while submitting).
   */
  submittingButtonLabel: PT.string,
  /**
   * styled-components theme object for feedbackForm.
   * e.g. { popper: { zIndex: 999 } }
   */
  theme: PT.object,
  translationMap: PT.object
};
FeedbackForm.defaultProps = {
  anchorElement: undefined,
  cancelButtonLabel: undefined,
  closeButtonLabel: undefined,
  defaultValidationErrorMessage: undefined,
  defaultValues: undefined,
  isIntroSkipped: false,
  isMobile: undefined,
  type: undefined,
  locale: undefined,
  mobileCloseButtonLabel: undefined,
  mobileShareButtonLabel: undefined,
  onClickResubmit: noop,
  onClose: noop,
  onLoadFailed: undefined,
  onLoadSucceeded: undefined,
  popperOffset: [0, 0],
  popperPlacement: 'left-end',
  popperStrategy: 'fixed',
  renderDisclaimer: undefined,
  renderHeader: undefined,
  renderIntro: undefined,
  renderPrompt: undefined,
  renderSuccess: undefined,
  renderError: undefined,
  resetOnClose: undefined,
  resubmitButtonLabel: undefined,
  resubmitEnabled: true,
  submitButtonLabel: undefined,
  submittingButtonLabel: undefined,
  theme: undefined,
  translationMap: undefined
};

export { FeedbackForm as F };
//# sourceMappingURL=FeedbackForm-BwbJ5VGx.js.map
